import styled from '@emotion/styled';
import { Field, PlanBadge, Stack } from '@hotjar/hotsauce';
import { useValidNumber } from '@hotjar/feedback/hooks/useValidNumber';
import { usePlanForFeature } from '@hotjar/webapp/hooks/usePlanForFeature';
import { useAskFeature } from '@hotjar/feedback';
import { useTranslation } from '@hotjar/translations';
import { SuffixedNumberInput } from '../../SuffixedNumberInput/SuffixedNumberInput';

const PlanWrapper = styled(Stack)`
  align-items: center;
`;

const MIN_VALUE = 1;
const MAX_VALUE = 100;

export function validateTrafficCoverage(coverage: number | undefined): boolean {
  return (
    coverage !== undefined && coverage >= MIN_VALUE && coverage <= MAX_VALUE && coverage % 1 === 0
  );
}

export interface TrafficCoverageProps {
  widget: 'feedback' | 'survey' | 'invitation';
  percentage: number | undefined;
  onPercentageChanged: (percentage: number | undefined) => void;
  onUpsell: () => void;
}

export const TrafficCoverage = ({
  widget,
  percentage,
  onPercentageChanged,
  onUpsell,
}: TrafficCoverageProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  const id = 'traffic-coverage-input';

  const requiredPlan = usePlanForFeature('targeting.cap');
  const hasRequiredPlan = useAskFeature('targeting.cap');

  const [currentPercentage, onCurrentPercentageChanged] = useValidNumber(
    percentage,
    onPercentageChanged,
  );

  const isInvalid = !validateTrafficCoverage(percentage);

  return (
    <Field
      isRequired
      isInvalid={isInvalid}
      invalidText={t('wizard.section.targeting.coverage.error', {
        minValue: MIN_VALUE,
        maxValue: MAX_VALUE,
      })}
      inputId={id}
      labelText={t('wizard.section.targeting.coverage.label')}
      helperText={t('wizard.section.targeting.coverage.description', {
        widgetType: widget === 'feedback' ? 'feedback widget' : widget,
      })}
      size="l"
    >
      <PlanWrapper>
        <SuffixedNumberInput
          suffix="%"
          id={id}
          value={currentPercentage}
          onChange={hasRequiredPlan ? onCurrentPercentageChanged : onUpsell}
          min={MIN_VALUE}
          max={MAX_VALUE}
          step="1"
          isInvalid={isInvalid}
          valueSize={3}
        />

        {!hasRequiredPlan && requiredPlan !== undefined && (
          <PlanBadge kind={requiredPlan} size="s" onClick={onUpsell} />
        )}
      </PlanWrapper>
    </Field>
  );
};
